/**
 * Global Cart Manager for PIANO theme
 * Handles cart updates across all pages using Shopify Cart API
 */

class CartManager {
  constructor() {
    this.cartData = null;
    this.badges = [];
    this.isUpdating = false;
    this.refreshRequested = false;

    this.init();
  }

  init() {
    // Initial cart fetch
    this.fetchCart();

    // Set up event listeners
    this.setupEventListeners();

    // Set up periodic cart check (every 30 seconds)
    setInterval(() => this.fetchCart(), 30000);
  }

  setupEventListeners() {
    // Listen for cart updates from other parts of the site
    document.addEventListener("cart-updated", () => this.fetchCart());
    document.addEventListener("cart-popup", () => this.fetchCart());

    // Listen for Shopify's native cart events
    document.addEventListener("cart:updated", () => this.fetchCart());
    document.addEventListener("cart:refresh", () => this.fetchCart());

    // Intercept cart-related form submissions
    document.addEventListener("submit", (e) => this.handleFormSubmit(e));

    // Intercept cart-related link clicks
    document.addEventListener("click", (e) => this.handleLinkClick(e));
  }

  async fetchCart() {
    if (this.isUpdating) {
      this.refreshRequested = true;
      return;
    }

    this.isUpdating = true;

    try {
      const response = await fetch("/cart.js");
      if (!response.ok) throw new Error("Failed to fetch cart");

      this.cartData = await response.json();
      this.updateBadges();

    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      this.isUpdating = false;
      if (this.refreshRequested) {
        this.refreshRequested = false;
        await this.fetchCart();
      }
    }
  }

  updateBadges() {
    if (!this.cartData) return;

    // Find all cart badges on the page
    this.badges = document.querySelectorAll("[data-cart-badge]");

    // Calculate item count from items array if item_count is not available
    let itemCount = this.cartData.item_count;
    if (itemCount === undefined && this.cartData.items) {
      itemCount = this.cartData.items.reduce(
        (total, item) => total + item.quantity,
        0
      );
    }

    this.badges.forEach((badge) => {
      if (itemCount > 0) {
        badge.textContent = itemCount;
        badge.style.display = "flex";
        badge.style.visibility = "visible";
        badge.style.opacity = "1";
        console.log(`Updated badge to show ${itemCount} items`);
      } else {
        badge.style.display = "none";
        badge.style.visibility = "hidden";
      }
    });

    // Dispatch event for other components
    window.dispatchEvent(
      new CustomEvent("cart-badge-updated", {
        detail: { itemCount: itemCount },
      })
    );
  }

  handleFormSubmit(e) {
    const form = e.target;
    const action = form.getAttribute("action");

    // Intercept cart-related form submissions
    if (
      action &&
      (action.includes("/cart/change") ||
        action.includes("/cart/add") ||
        action.includes("/cart/update"))
    ) {
      e.preventDefault();

      const formData = new FormData(form);
      const url = form.action;
      const method = form.method || "POST";

      // Convert to AJAX request using Shopify Cart API
      fetch(url, {
        method: method,
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      })
        .then(async (response) => {
          if (!response.ok) {
            let description = "";
            try {
              const j = await response.json();
              description = j.description || j.message || "";
            } catch (e) {
              // odpowiedź nie-JSON (np. HTML) — zostawiamy pusty opis
            }
            this.showCartError(this.mapCartError(response.status, description));
            this.fetchCart(); // przywróć prawdziwy stan koszyka w UI
            return null;
          }
          return response.json();
        })
        .then((data) => {
          if (!data) return;
          this.cartData = data;
          this.updateBadges();
          window.dispatchEvent(
            new CustomEvent("cart-updated", { detail: data })
          );
        })
        .catch((error) => {
          console.error("Error updating cart:", error);
          this.showCartError("generic");
          this.fetchCart();
        });
    }
  }

  handleLinkClick(e) {
    const link = e.target.closest("a");
    if (!link) return;

    const href = link.getAttribute("href");
    if (
      href &&
      (href.includes("/cart/change") ||
        href.includes("/cart/add") ||
        href.includes("/cart/update"))
    ) {
      e.preventDefault();

      // Handle as AJAX request
      fetch(href, {
        method: "GET",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      })
        .then(async (response) => {
          if (!response.ok) {
            let description = "";
            try {
              const j = await response.json();
              description = j.description || j.message || "";
            } catch (e) {
              // odpowiedź nie-JSON — pusty opis
            }
            this.showCartError(this.mapCartError(response.status, description));
            this.fetchCart();
            return null;
          }
          return response.json();
        })
        .then((data) => {
          if (!data) return;
          this.cartData = data;
          this.updateBadges();
          window.dispatchEvent(
            new CustomEvent("cart-updated", { detail: data })
          );
        })
        .catch((error) => {
          console.error("Error updating cart:", error);
          this.showCartError("generic");
          this.fetchCart();
        });
    }
  }

  // Public method to add item to cart
  async addToCart(variantId, quantity = 1, properties = {}) {
    try {
      // Ensure variant ID is a number
      const numericVariantId = parseInt(variantId, 10);
      console.log("Adding to cart:", { variantId: numericVariantId, quantity, properties });
      
      if (isNaN(numericVariantId)) {
        throw new Error(`Invalid variant ID: ${variantId}`);
      }
      
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: [
            {
              id: numericVariantId,
              quantity: quantity,
              properties: properties,
            },
          ],
        }),
      });

      console.log("Cart add response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          "Cart add failed with status:",
          response.status,
          "Error:",
          errorText
        );

        let description = errorText;
        try {
          const errorData = JSON.parse(errorText);
          description = errorData.description || errorData.message || errorText;
        } catch (e) {
          // nie-JSON — zostaje surowy tekst tylko do rozpoznania typu
        }

        const err = new Error(`Failed to add to cart: ${response.status}`);
        err.status = response.status;
        err.cartErrorKey = this.mapCartError(response.status, description);
        throw err;
      }

      const data = await response.json();
      console.log("Cart add response data:", data);

      // Fetch the full cart data instead of using the add response
      await this.fetchCart();

      window.dispatchEvent(
        new CustomEvent("cart-updated", { detail: this.cartData })
      );
      return this.cartData;
    } catch (error) {
      console.error("Error adding to cart:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        variantId: variantId,
        quantity: quantity
      });
      throw error;
    }
  }

  // Public method to update cart item
  async updateCartItem(key, quantity) {
    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
        }),
      });

      if (!response.ok) throw new Error("Failed to update cart");

      const data = await response.json();

      // Fetch the full cart data instead of using the change response
      await this.fetchCart();

      window.dispatchEvent(
        new CustomEvent("cart-updated", { detail: this.cartData })
      );
      return this.cartData;
    } catch (error) {
      console.error("Error updating cart:", error);
      throw error;
    }
  }

  // Public method to remove item from cart
  async removeFromCart(key) {
    return this.updateCartItem(key, 0);
  }

  // Public method to clear cart
  async clearCart() {
    try {
      const response = await fetch("/cart/clear.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error("Failed to clear cart");

      const data = await response.json();

      // Fetch the full cart data instead of using the clear response
      await this.fetchCart();

      window.dispatchEvent(
        new CustomEvent("cart-updated", { detail: this.cartData })
      );
      return this.cartData;
    } catch (error) {
      console.error("Error clearing cart:", error);
      throw error;
    }
  }

  // Get current cart data
  getCartData() {
    return this.cartData;
  }

  // Get current item count
  getItemCount() {
    if (!this.cartData) return 0;

    let itemCount = this.cartData.item_count;
    if (itemCount === undefined && this.cartData.items) {
      itemCount = this.cartData.items.reduce(
        (total, item) => total + item.quantity,
        0
      );
    }

    return itemCount || 0;
  }

  // Fallback komunikatów, gdy window.themeStrings.cartErrors nie dotarł (parytet z locale)
  static CART_ERROR_FALLBACK = {
    too_many:
      "Nie możemy dodać tylu sztuk — dostępna ilość jest ograniczona. Zmniejsz liczbę i spróbuj ponownie.",
    sold_out: "Ten produkt jest już wyprzedany.",
    variant_unavailable: "Ten produkt jest chwilowo niedostępny.",
    quantity_limit:
      "W koszyku masz już maksymalną dostępną ilość tego produktu.",
    update_failed:
      "Nie udało się zaktualizować ilości. Odśwież stronę i spróbuj ponownie.",
    remove_failed: "Nie udało się usunąć produktu. Spróbuj ponownie.",
    empty_cart: "Twój koszyk jest pusty.",
    generic: "Coś poszło nie tak. Spróbuj ponownie za chwilę.",
    ok: "OK",
  };

  // Zamienia (status HTTP, opis Shopify) na klucz komunikatu.
  // description służy TYLKO do rozpoznania typu — nigdy nie trafia do UI.
  mapCartError(status, description) {
    const d = (description || "").toLowerCase();
    if (status === 422) {
      if (d.includes("sold out") || d.includes("wyprzeda")) return "sold_out";
      if (d.includes("maksymaln")) return "quantity_limit";
      return "too_many"; // "you can't add more" / limit stanu
    }
    if (status === 404) return "variant_unavailable";
    return "generic"; // 400, 5xx, nieznane
  }

  // Pokazuje komunikat błędu koszyka w GLOBALNYM popupie (nad mini-koszykiem).
  showCartError(key) {
    const strings =
      (window.themeStrings && window.themeStrings.cartErrors) || {};
    const fallback = CartManager.CART_ERROR_FALLBACK;
    const message = strings[key] || fallback[key] || fallback.generic;
    const label = strings.ok || fallback.ok;
    try {
      if (window.Alpine && Alpine.store && Alpine.store("popup")) {
        // 'text-center' — komunikat błędu jest wyśrodkowany (także gdy zawija się na kilka linii)
        Alpine.store("popup").open(message, [{ label: label }], "text-center");
        return;
      }
    } catch (e) {
      // brak Alpine/popup — spadamy do alert()
    }
    window.alert(message);
  }

  // Force refresh badges (useful for debugging)
  forceRefreshBadges() {
    this.updateBadges();
  }

  // Debug method to check badge state
  debugBadges() {
    const badges = document.querySelectorAll("[data-cart-badge]");
    console.log(`Found ${badges.length} cart badges on page`);
    badges.forEach((badge, index) => {
      console.log(`Badge ${index}:`, {
        text: badge.textContent,
        display: badge.style.display,
        visibility: badge.style.visibility,
        opacity: badge.style.opacity,
        visible: badge.offsetParent !== null,
      });
    });
  }
}

// Exactly one manager: duplicate initialization creates competing refresh loops.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    window.cartManager = new CartManager();
  });
} else {
  window.cartManager = new CartManager();
}
